import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { createPortal } from 'react-dom'
import styled, { keyframes } from 'styled-components'
import CloseRoundedIcon from '@mui/icons-material/CloseRounded'
import UploadFileRoundedIcon from '@mui/icons-material/UploadFileRounded'
import CheckRoundedIcon from '@mui/icons-material/CheckRounded'
import SearchRoundedIcon from '@mui/icons-material/SearchRounded'
import ApartmentRoundedIcon from '@mui/icons-material/ApartmentRounded'
import OpenInNewRoundedIcon from '@mui/icons-material/OpenInNewRounded'
import AutoAwesomeRoundedIcon from '@mui/icons-material/AutoAwesomeRounded'
import { fetchProgramme } from 'services/ProgrammeService'
import { fetchBpUnits } from 'services/unitsService'
import { cadenceLabel, PENALTY_SEVERITIES } from 'features/compliance/obligationConstants'
import PerUnitSatisfyExplainer from 'features/compliance/PerUnitSatisfyExplainer'

const STATE = {
  covered: { label: 'Covered', fg: '#15803d', bg: '#dcfce7' },
  expired: { label: 'Overdue', fg: '#b00020', bg: '#fdecea' },
  pending: { label: 'Awaiting', fg: '#d97706', bg: '#fef3c7' },
}

const CRIT = {
  critical: { fg: '#b00020', bg: '#fdecea', label: 'Critical' },
  high:     { fg: '#c2410c', bg: '#ffedd5', label: 'High' },
  medium:   { fg: '#d97706', bg: '#fef3c7', label: 'Medium' },
  low:      { fg: '#15803d', bg: '#dcfce7', label: 'Low' },
}

const PerUnitEvidenceModal = ({ bpId, obligation, onClose, onChanged }) => {
  const [coverage, setCoverage] = useState(obligation?.unitCoverage || null)
  const [details, setDetails] = useState(obligation)
  const [fileType, setFileType] = useState(
    obligation?.fileType || (obligation?.fileTypeId ? { id: obligation.fileTypeId, name: obligation.fileTypeName } : null)
  )
  const [unitNames, setUnitNames] = useState({})
  const [loading, setLoading] = useState(!obligation?.unitCoverage)
  const [search, setSearch] = useState('')
  const [limit, setLimit] = useState(25)

  const refreshCoverage = useCallback((silent = false) => {
    if (!silent) setLoading(true)
    return fetchProgramme(bpId)
      .then(res => {
        const all = (res?.data?.data?.domains || []).flatMap(d => d.obligations || [])
        const found = all.find(o => o.id === obligation.id)
        if (found) {
          setCoverage(found.unitCoverage || null)
          setDetails(prev => ({ ...prev, ...found }))
          if (found.fileType) setFileType(found.fileType)
        }
      })
      .catch(() => {})
      .finally(() => setLoading(false))
  }, [bpId, obligation.id])

  useEffect(() => {
    fetchBpUnits({ bpId })
      .then(res => {
        const list = res?.data?.data || res?.data || []
        const map = {}
        ;(Array.isArray(list) ? list : []).forEach(u => { map[u.id] = u.name || u.displayName || u.address || 'Unit' })
        setUnitNames(map)
      })
      .catch(() => {})
    if (!obligation?.unitCoverage) refreshCoverage()
  }, [bpId, obligation, refreshCoverage])

  useEffect(() => {
    const onFocus = () => { refreshCoverage(true).then(() => onChanged?.()) }
    window.addEventListener('focus', onFocus)
    return () => window.removeEventListener('focus', onFocus)
  }, [refreshCoverage, onChanged])

  const units = coverage?.units || []
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase()
    if (!q) return units
    return units.filter(u => (unitNames[u.unitId || u.unit_id] || 'Unit').toLowerCase().includes(q))
  }, [units, search, unitNames])
  const visible = filtered.slice(0, limit)

  const crit = CRIT[details.criticality] || null
  const penalty = details.penaltySeverity && details.penaltySeverity !== 'none'
    ? (PENALTY_SEVERITIES[details.penaltySeverity]?.label || details.penaltySeverity)
    : null

  const uploadFor = uid => {
    const typeId = fileType?.id
    const typeQ = typeId ? `&type=${encodeURIComponent(typeId)}` : ''
    window.open(`/files/upload/${bpId}?unit=${encodeURIComponent(uid)}&method=traditional${typeQ}`, '_blank')
  }

  return createPortal(
    <Backdrop onClick={onClose}>
      <Panel onClick={e => e.stopPropagation()}>
        <Header>
          <HeaderText>
            <Eyebrow>Per-unit evidence</Eyebrow>
            <Title>{obligation.name}</Title>
            <Sub>
              This is a <strong>per-unit</strong> document. Every Unit Passport needs its own file
              {fileType?.name ? <> of type <strong>{fileType.name}</strong></> : null}. Evidence is saved on each unit, not the building.
            </Sub>
            <ExplainRow>
              <PerUnitSatisfyExplainer taskKind={details?.obligationKind === 'task'} />
            </ExplainRow>
          </HeaderText>
          <CloseBtn onClick={onClose} aria-label='Close'><CloseRoundedIcon style={{ fontSize: 20 }} /></CloseBtn>
        </Header>

        <Details>
          <MetaRow>
            {crit && <MPill $fg={crit.fg} $bg={crit.bg}>{crit.label}</MPill>}
            <MPill $fg='#0e7490' $bg='#e0f2fe'>{details.scope === 'unit' ? 'Per-unit document' : 'Document'}</MPill>
            <MText>{details.cadenceDays ? cadenceLabel(details.cadenceDays) : 'One-off'}</MText>
            {details.taskDomainName ? <MText>{details.taskDomainName}</MText> : null}
            {typeof details.isStatutory === 'boolean' && (
              <MPill $fg={details.isStatutory ? '#1f5fbf' : '#64748b'} $bg={details.isStatutory ? '#e8f1fd' : '#f1f3f7'}>
                {details.isStatutory ? 'Statutory' : 'Non-statutory'}
              </MPill>
            )}
            {penalty && <MText>Penalty: {penalty}</MText>}
          </MetaRow>
          {details.description && <DetailText>{details.description}</DetailText>}
          {details.regulatoryFramework && (
            <LegRow>
              <LegLabel>Legislation</LegLabel>
              <LegVal>{details.regulatoryFramework}</LegVal>
              {details.citationUrl && (
                <LegLink href={details.citationUrl} target='_blank' rel='noopener noreferrer'>
                  Read <OpenInNewRoundedIcon style={{ fontSize: 12 }} />
                </LegLink>
              )}
            </LegRow>
          )}
        </Details>

        <AutoNote>
          <AutoAwesomeRoundedIcon style={{ fontSize: 15 }} />
          <span>
            No manual linking. Upload a matching-type document onto a unit and it's <strong>detected automatically</strong> as
            that unit's evidence, then reviewed before it counts toward the score.
          </span>
        </AutoNote>

        {coverage && (
          <Summary>
            <strong>{coverage.covered}/{coverage.applicable}</strong> units covered
            {coverage.pending > 0 ? ` · ${coverage.pending} awaiting` : ''}
            {coverage.expired > 0 ? ` · ${coverage.expired} overdue` : ''}
          </Summary>
        )}

        <Body>
          {units.length > 10 && (
            <SearchWrap>
              <SearchRoundedIcon style={{ fontSize: 18, color: '#94a3b8' }} />
              <SearchInput value={search} onChange={e => { setSearch(e.target.value); setLimit(25) }} placeholder='Search units…' />
            </SearchWrap>
          )}
          {loading ? (
            [0, 1, 2, 3].map(i => <Skeleton key={i} />)
          ) : units.length === 0 ? (
            <Muted>No units apply to this obligation.</Muted>
          ) : (
            <>
              <List onScroll={e => {
                const el = e.currentTarget
                if (el.scrollTop + el.clientHeight >= el.scrollHeight - 40) setLimit(l => (l < filtered.length ? l + 25 : l))
              }}>
                {visible.map(u => {
                  const uid = u.unitId || u.unit_id
                  const st = STATE[u.state] || STATE.pending
                  const covered = u.state === 'covered'
                  const expiry = u.expiryDate || u.expiry_date
                  return (
                    <Row key={uid} $covered={covered}>
                      <UnitIcon><ApartmentRoundedIcon style={{ fontSize: 16 }} /></UnitIcon>
                      <UnitName>{unitNames[uid] || 'Unit'}</UnitName>
                      <StateChip $fg={st.fg} $bg={st.bg}>
                        {covered && <CheckRoundedIcon style={{ fontSize: 12 }} />}{st.label}
                      </StateChip>
                      {expiry && <Expiry>until {new Date(expiry).toLocaleDateString('en-GB')}</Expiry>}
                      <UploadBtn onClick={() => uploadFor(uid)}>
                        <UploadFileRoundedIcon style={{ fontSize: 14 }} /> {covered ? 'Replace' : 'Upload'}
                      </UploadBtn>
                    </Row>
                  )
                })}
              </List>
              <Meta>
                Showing {visible.length} of {filtered.length}
                {filtered.length !== units.length ? ` (filtered from ${units.length})` : ''}
              </Meta>
            </>
          )}
        </Body>

        <Footer>
          <Hint>Upload opens the unit's uploader in a new tab; this list refreshes when you return.</Hint>
          <DoneBtn onClick={onClose}>Done</DoneBtn>
        </Footer>
      </Panel>
    </Backdrop>,
    document.body
  )
}

export default PerUnitEvidenceModal

const pulse = keyframes`0%{opacity:.55}50%{opacity:1}100%{opacity:.55}`

const Backdrop = styled.div`
  position: fixed; inset: 0; z-index: 100001;
  background: rgba(12, 18, 41, 0.5);
  display: flex; align-items: center; justify-content: center; padding: 20px;
`
const Panel = styled.div`
  background: #fff; border-radius: 14px;
  width: 100%; max-width: 560px; max-height: calc(100vh - 48px);
  display: flex; flex-direction: column; overflow: hidden;
  box-shadow: 0 20px 48px rgba(0,0,0,0.22);
`
const Header = styled.div` display: flex; align-items: flex-start; gap: 14px; padding: 18px 20px 12px; `
const HeaderText = styled.div` flex: 1; min-width: 0; display: flex; flex-direction: column; gap: 4px; `
const Eyebrow = styled.span`
  font-size: 10.5px; font-weight: 800; letter-spacing: 0.12em; text-transform: uppercase; color: #7C5FE0;
`
const Title = styled.h3` margin: 0; font-size: 16px; font-weight: 700; color: #101828; `
const ExplainRow = styled.div` margin-top: 8px; display: flex; `
const Sub = styled.p` margin: 0; font-size: 12.5px; line-height: 1.5; color: #667085; strong { color: #1d2433; } `
const CloseBtn = styled.button`
  border: none; background: #f2f4f7; color: #475467; cursor: pointer;
  width: 30px; height: 30px; border-radius: 8px; flex-shrink: 0;
  display: inline-flex; align-items: center; justify-content: center;
  &:hover { background: #e6e9ee; }
`
const Summary = styled.div`
  margin: 10px 20px 0; padding: 8px 12px; border-radius: 9px;
  background: #f8f9fb; border: 1px solid #eef0f3;
  font-size: 12.5px; color: #475467; strong { color: #101828; }
`
const Details = styled.div`
  margin: 12px 20px 0; padding: 12px 14px; border-radius: 10px;
  background: #fbfbfc; border: 1px solid #eef0f3;
  display: flex; flex-direction: column; gap: 8px;
`
const MetaRow = styled.div` display: flex; flex-wrap: wrap; align-items: center; gap: 7px; `
const MPill = styled.span`
  font-size: 10.5px; font-weight: 700; padding: 2px 8px; border-radius: 999px;
  color: ${({ $fg }) => $fg}; background: ${({ $bg }) => $bg};
`
const MText = styled.span` font-size: 11.5px; color: #667085; `
const DetailText = styled.p` margin: 0; font-size: 12.5px; line-height: 1.55; color: #475467; `
const LegRow = styled.div` display: flex; align-items: center; gap: 8px; flex-wrap: wrap; `
const LegLabel = styled.span` font-size: 10px; font-weight: 800; letter-spacing: 0.08em; text-transform: uppercase; color: #98a2b3; `
const LegVal = styled.span` font-size: 12px; font-weight: 600; color: #1d2433; `
const LegLink = styled.a`
  display: inline-flex; align-items: center; gap: 3px;
  font-size: 11.5px; font-weight: 700; color: #2aa6b3; text-decoration: none;
  &:hover { text-decoration: underline; }
`
const AutoNote = styled.div`
  display: flex; align-items: flex-start; gap: 8px;
  margin: 10px 20px 0; padding: 9px 12px; border-radius: 10px;
  background: #f2fbfc; border: 1px solid #cdeef1;
  font-size: 12px; line-height: 1.5; color: #134e4a;
  svg { color: #0e7490; flex-shrink: 0; margin-top: 1px; }
  strong { font-weight: 800; color: #0f766e; }
`
const Body = styled.div` flex: 1; min-height: 0; padding: 12px 20px; overflow-y: auto; display: flex; flex-direction: column; gap: 8px; `
const SearchWrap = styled.div`
  display: flex; align-items: center; gap: 8px; padding: 8px 12px; border: 1px solid #ececec; border-radius: 10px;
`
const SearchInput = styled.input`
  font: inherit; flex: 1; min-width: 0; border: none; outline: none; background: transparent; font-size: 13px; color: #1d2433;
`
const List = styled.div` display: flex; flex-direction: column; gap: 6px; max-height: 46vh; overflow-y: auto; `
const Row = styled.div`
  display: flex; align-items: center; gap: 10px;
  padding: 9px 11px; border-radius: 10px;
  border: 1px solid ${({ $covered }) => ($covered ? '#e6f3ec' : '#ececec')};
  background: ${({ $covered }) => ($covered ? '#f6fbf8' : '#fff')};
`
const UnitIcon = styled.span`
  display: inline-flex; align-items: center; justify-content: center;
  width: 26px; height: 26px; border-radius: 7px; background: #eef0f3; color: #667085; flex-shrink: 0;
`
const UnitName = styled.span` flex: 1; min-width: 0; font-size: 13px; font-weight: 600; color: #1d2433; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; `
const StateChip = styled.span`
  display: inline-flex; align-items: center; gap: 3px; flex-shrink: 0;
  font-size: 10.5px; font-weight: 800; padding: 2px 9px; border-radius: 999px;
  color: ${({ $fg }) => $fg}; background: ${({ $bg }) => $bg};
`
const Expiry = styled.span` font-size: 10.5px; color: #8a909b; flex-shrink: 0; `
const UploadBtn = styled.button`
  font: inherit; flex-shrink: 0;
  display: inline-flex; align-items: center; gap: 5px;
  border: 1px solid #2aa6b3; background: #2aa6b3; color: #fff;
  font-size: 12px; font-weight: 700; padding: 6px 11px; border-radius: 8px; cursor: pointer;
  &:hover { filter: brightness(0.96); }
`
const Meta = styled.div` font-size: 10.5px; color: #8a909b; padding-top: 2px; `
const Muted = styled.div` padding: 22px; text-align: center; font-size: 13px; color: #94a3b8; `
const Skeleton = styled.div` height: 44px; border-radius: 10px; background: #f1f5f9; animation: ${pulse} 1.4s ease-in-out infinite; `
const Footer = styled.div`
  display: flex; align-items: center; justify-content: space-between; gap: 14px;
  padding: 12px 20px; border-top: 1px solid #eef0f3; background: #fcfcfd;
`
const Hint = styled.span` font-size: 11.5px; color: #8a909b; `
const DoneBtn = styled.button`
  border: 1px solid #101828; background: #101828; color: #fff;
  font: inherit; font-size: 13px; font-weight: 700; padding: 8px 16px; border-radius: 9px; cursor: pointer;
  &:hover { background: #1d2433; }
`
